import { put, delay, takeEvery, all, call, select, race, cancelled, fork, join } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setHomePictreeUser,
  setHomePictreeCheckIn,
  setHomePictreePhotoShoot,
  setHomePictreeReportTsuru,
  patchHomePictreePhotoShoot,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";

// 共通：メトリクス1個を処理するユーティリティ（JS版）
function* runMetricTask(key, effect, transform, alsoPatch) {
  const now = (typeof performance !== 'undefined' && performance.now) ? () => performance.now() : () => Date.now();
  const t0 = now();
  try {
    const raw = yield effect;
    const value = transform ? transform(raw) : raw;

    const extra = alsoPatch ? (alsoPatch(value) || {}) : {};
    yield put(patchHomePictreePhotoShoot({ [key]: value, ...extra }));

    const t1 = now();
    yield put(patchHomePictreePhotoShoot({ timings: { [key]: Math.round(t1 - t0) } }));
  } catch (e) {
    const t1 = now();
    yield put(patchHomePictreePhotoShoot({
      [key]: 'error',
      timings: { [key]: Math.round(t1 - t0) },
      errors:  { [key]: (e && (e.message || e.errorMessage)) || 'failed' },
    }));
  }
}

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000,   // 15s
  retries: 1,
};

// ローカル検証用: query1（台帳あり/報告あり total）を固定値で上書きする
const USE_MOCK_TSURU_LEDGER_TOTAL = false;
const MOCK_TSURU_LEDGER_TOTAL = [
  {
    // saitama 5/19 1095
    // ibaraki 5/19 2890
    // chiba 5/19 5727
    "LedgerTotal": 5727,
  },
];

// ローカル検証用: query3（通常電柱/報告あり total）を固定値で上書きする
const USE_MOCK_TSURU_NO_LEDGER_TOTAL = false;
const MOCK_TSURU_NO_LEDGER_TOTAL = [
  {
    // saitama 5/7 147347, 5/13 164266, 5/19 177383,
    // ibaraki 5/19 86984,
    // chiba 5/7 226212, 318666, 5/13 390353, 5/19 464379
    "NoLedgerTotal": 464379,
  },
];

// ローカル検証用: query4（通常電柱/報告なし）を固定値で上書きする
const USE_MOCK_TSURU_NO_LEDGER_NO_REPORT = false;
const MOCK_TSURU_NO_LEDGER_NO_REPORT = [
  {
    // saitama 100440 143435 5/13 159425, 5/19 171893
    // ibaraki 5/19 81327
    // chiba 218179 307196 5/13 376000, 5/19 446916
    "count-NoLedgerAndNoReport": 446916,
  },
];

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const startPrevDate = state => state.page.prevDateRangePicTree[0];
export const endPrevDate = state => state.page.prevDateRangePicTree[1];
export const selectedSeason = state => state.pictree.selectedSeason;
export const homePictreePhotoShootSelector = (state) => state.pictree.homePhotoShoot;

export function* getPictreeDataHomeUser(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const datePrevFrom = yield select(startPrevDate);
    const datePrevTo = yield select(endPrevDate);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected for PicTree home photo shoot");
      yield put(setHomePictreePhotoShoot({
        countDenchu: 0,
        countUniqueUser: 0,
        countPic: 0,
        countAsset: 0,
        countDupeDenchu: 0,
        countReviewOk: 0,
        progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
        timings: {},
        errors: { season: "No seasons selected" },
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    // Date Handling
    const getDateDiff = (dateFrom, subDays) => {
      return moment(dateFrom).tz("Asia/Tokyo").subtract(subDays, 'days').format('YYYY-MM-DD');
    }
    const wauPrevEnd = getDateDiff(rangeTarget[1], 7);
    const wauPrevBegin = getDateDiff(wauPrevEnd, 6);
    const mauPrevEnd = getDateDiff(rangeTarget[1], 30);
    const mauPrevBegin = getDateDiff(mauPrevEnd, 29);

    const sqlQueryUsers = {"query": `SELECT COUNT(*) AS total_registrations FROM player`};
    const sqlQueryUsersInRange = {"query": `
        SELECT COUNT(*) AS registrations_in_range
        FROM player
        WHERE CONVERT_TZ(registered_datetime, 'UTC', 'Asia/Tokyo') BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59';
    `};
    const sqlQueryUsersInRangePrev = {"query": `
        SELECT COUNT(*) AS registrations_in_range_prev
        FROM player
        WHERE CONVERT_TZ(registered_datetime, 'UTC', 'Asia/Tokyo') BETWEEN '${datePrevFrom} 00:00:00' AND '${datePrevTo} 23:59:59';
    `};
    const sqlQueryCountDauUser = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${rangeTarget[1]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountDauUserPrev = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${getDateDiff(rangeTarget[1], 1)} 00:00:00' AND '${getDateDiff(rangeTarget[1], 1)} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountWauUser = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${getDateDiff(rangeTarget[1], 7)} 00:00:00' AND '${rangeTarget[1]} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountWauUserPrev = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${wauPrevBegin} 00:00:00' AND '${wauPrevEnd} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountMauUser = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${getDateDiff(rangeTarget[1], 30)} 00:00:00' AND '${rangeTarget[1]} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountMauUserPrev = {
      "query": `SELECT COUNT(DISTINCT deviceId) FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${mauPrevBegin} 00:00:00' AND '${mauPrevEnd} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };

    const sqlQueryUsersPmid = {"query": `
      SELECT COUNT(*) AS total_pmid_sync
      FROM player ply
      JOIN account_connect_for_playmining acnp ON ply.id = acnp.player_id
      JOIN player_event	pe ON ply.id = pe.player_id
      WHERE pe.type = 'CONNECTED_TO_PLAYMINING'
    `};
    const sqlQueryUsersPmidInRange = {"query": `
      SELECT COUNT(*) AS pmid_sync_in_range
      FROM player ply
      JOIN account_connect_for_playmining acnp ON ply.id = acnp.player_id
      JOIN player_event	pe ON ply.id = pe.player_id
      WHERE CONVERT_TZ(occurred_datetime, 'UTC', 'Asia/Tokyo') BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
      AND pe.type = 'CONNECTED_TO_PLAYMINING';
    `};
    const sqlQueryUsersPmidInRangePrev = {"query": `
      SELECT COUNT(*) AS pmid_sync_in_range_prev
      FROM player ply
      JOIN account_connect_for_playmining acnp ON ply.id = acnp.player_id
      JOIN player_event	pe ON ply.id = pe.player_id
      WHERE CONVERT_TZ(occurred_datetime, 'UTC', 'Asia/Tokyo') BETWEEN '${datePrevFrom} 00:00:00' AND '${datePrevTo} 23:59:59'
      AND pe.type = 'CONNECTED_TO_PLAYMINING';
    `};
    const [dataHomeUserTotal, dataHomeUser, dataHomeUserPrev,
        dataHomeDau, dataHomeDauPrev, dataHomeWau, dataHomeWauPrev, dataHomeMau, dataHomeMauPrev,
        dataTotalPmidSync, dataPmidSync, dataPmidSyncPrev,] = yield all([
        call(getDataFromSxi, sqlQueryUsers, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(getDataFromSxi, sqlQueryUsersInRange, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(getDataFromSxi, sqlQueryUsersInRangePrev, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(getDataFromRDS, sqlQueryCountDauUser, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromRDS, sqlQueryCountDauUserPrev, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromRDS, sqlQueryCountWauUser, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromRDS, sqlQueryCountWauUserPrev, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromRDS, sqlQueryCountMauUser, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromRDS, sqlQueryCountMauUserPrev, ENDPOINT.GET_DATA_USERS, jwtToken),
        call(getDataFromSxi, sqlQueryUsersPmid, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(getDataFromSxi, sqlQueryUsersPmidInRange, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(getDataFromSxi, sqlQueryUsersPmidInRangePrev, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
    ]);
    const totalUsers = dataHomeUserTotal === null ? 0 : dataHomeUserTotal[0]["total_registrations"];
    const countUsersInRange = dataHomeUser === null ? 0 : dataHomeUser[0]["registrations_in_range"];
    const countUsersInRangePrev = dataHomeUserPrev === null ? 0 : dataHomeUserPrev[0]["registrations_in_range_prev"];

    const totalPmid = dataTotalPmidSync === null ? 0 : dataTotalPmidSync[0]["total_pmid_sync"];
    const countPmidInRange = dataPmidSync === null ? 0 : dataPmidSync[0]["pmid_sync_in_range"];
    const countPmidInRangePrev = dataPmidSyncPrev === null ? 0 : dataPmidSyncPrev[0]["pmid_sync_in_range_prev"];

    const countDau = JSON.parse(dataHomeDau.body);
    const countDauPrev = JSON.parse(dataHomeDauPrev.body);
    const countWau = JSON.parse(dataHomeWau.body);
    const countWauPrev = JSON.parse(dataHomeWauPrev.body);
    const countMau = JSON.parse(dataHomeMau.body);
    const countMauPrev = JSON.parse(dataHomeMauPrev.body);

    const homeUserData = {
        total: totalUsers,
        newTotal: countUsersInRange,
        newOrg: countUsersInRange,
        newOrgPrev: countUsersInRangePrev,
        pmidTotal: totalPmid,
        pmidNew: countPmidInRange,
        pmidPrev: countPmidInRangePrev,
        dau: countDau[0][0]["longValue"],
        dauPrev: countDauPrev[0][0]["longValue"],
        wau: countWau[0][0]["longValue"],
        wauPrev: countWauPrev[0][0]["longValue"],
        mau: countMau[0][0]["longValue"],
        mauPrev: countMauPrev[0][0]["longValue"],
    };

    yield put(setHomePictreeUser(homeUserData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

export function* getPictreeDataHomePhotoShootTepco(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData
          .map((s) => `'${s.seasonId}'`)
          .join(", ");
        return selectedSeasonData.length === 1
          ? `AND gameSpaceId = ${ids}`         // ids は "'id1'"
          : `AND (gameSpaceId IN (${ids}))`;     // ids は "'id1','id2',…"
      }
      return "";
    })();
    const seasonQuerySingleMulti = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData
          .map((s) => `'${s.seasonId}'`)
          .join(", ");
        return selectedSeasonData.length === 1
          ? `.game_space_id = ${ids}`         // ids は "'id1'"
          : `.game_space_id IN (${ids})`;     // ids は "'id1','id2',…"
      }
      return "";
    })();

    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    yield put(setHomePictreePhotoShoot({
      countDenchu: 'loading',
      countUniqueUser: 'loading',
      countPic: 'loading',
      countAsset: 'loading',
      countDupeDenchu: 'loading',
      countReviewOk: 'loading',
      progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
      timings: {}, errors: {},
    }));

    const sqlQueryCountPhotoDenchu = {
      "query": `
        SELECT COUNT(DISTINCT pae.power_asset_id) AS total_photo_power_assets
        FROM power_asset_event pae
        WHERE pae${seasonQuerySingleMulti}
          AND pae.type = 'PHOTOGRAPH'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime <  '${endUtc}';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountUniqueUser = {
      "query":  `
        SELECT COUNT(DISTINCT rr.player_id) AS unique_photographers
        FROM review_request_event rre
        JOIN review_request rr ON rr.id = rre.review_request_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        WHERE rr${seasonQuerySingleMulti}
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountPicCount = {
      "query": `
        SELECT
          SUM(picCount) AS total_pic_count
        FROM (
          SELECT picCount FROM DenchuPhotoShootDenchuEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
          BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
          AND property = 'tepco' ${seasonQuery}
        ) AS combined_data;`,
      "database": "KpiDashboard",
      "type": "list"
    };
    // dupe photoshoot - denchu
    const sqlQueryCountPhotoDupeDenchu = {
      "query": `
        SELECT COUNT(*) AS dupe_asset_count
        FROM (
          SELECT pae.power_asset_id
          FROM power_asset_event AS pae
          WHERE pae.type = 'PHOTOGRAPH'
            AND pae${seasonQuerySingleMulti}
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <   '${endUtc}'
          GROUP BY pae.power_asset_id
          HAVING COUNT(*) > 1
        ) AS t;
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountReviewOk = {
      "query": `
        SELECT COUNT(DISTINCT rr.power_asset_id) AS reviewOk_count
        FROM review_request_event rre
        JOIN review_request rr ON rre.review_request_id = rr.id
        WHERE rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        AND rre${seasonQuerySingleMulti};
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountAssetsByType = {
      "query": `SELECT pa.type, COUNT(*) AS count  FROM power_asset_state pas
                JOIN power_asset pa ON pa.id = pas.power_asset_id
                WHERE pas${seasonQuerySingleMulti}
                GROUP BY pa.type`,
      "database": "KpiDashboard",
      "type": "list"
    };

    // 2) 並列起動：forkで“完了した順に”patch
    const forks = [];

    // 電柱撮影基数
    forks.push(yield fork(
      runMetricTask,
      'countDenchu',
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      rows => rows[0].total_photo_power_assets,
      v => ({ countAsset: v }) // ★ ここが重要
    ));

    // ユニーク撮影者
    forks.push(yield fork(runMetricTask,
      'countUniqueUser',
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountUniqueUser, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      (rows) => rows[0]['unique_photographers']
    ));

    // 重複電柱数
    forks.push(yield fork(runMetricTask,
      'countDupeDenchu',
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPhotoDupeDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      (rows) => rows[0]['dupe_asset_count']
    ));

    // ReviewOK
    forks.push(yield fork(runMetricTask,
      'countReviewOk',
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryCountReviewOk, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      (row) => row[0]['reviewOk_count']
    ));

    // PicCount（Users系）
    forks.push(yield fork(runMetricTask,
      'countPic',
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPicCount, ENDPOINT.GET_DATA_USERS, jwtToken],
        USERS_LIGHT_RETRY
      ),
      (resp) => {
        const v = JSON.parse(resp.body)?.[0]?.[0]?.stringValue;
        return v === undefined ? 0 : parseInt(v, 10);
      }
    ));

    // 資産タイプ集計 → progressDenchu を patch
    forks.push(yield fork(function* () {
      const t0 = performance.now();
      try {
        const rows = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [sqlQueryCountAssetsByType, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );
        const assetMax = _.sumBy(rows, 'count');

        yield put(patchHomePictreePhotoShoot({
          progressDenchu: { max: assetMax, maxByasset: assetMax }
        }));

        const t1 = performance.now();
        yield put(patchHomePictreePhotoShoot({ timings: { assetsByType: Math.round(t1 - t0) } }));
      } catch (e) {
        const t1 = performance.now();
        yield put(patchHomePictreePhotoShoot({
          errors: { assetsByType: e?.message || 'failed' },
          timings: { assetsByType: Math.round(t1 - t0) },
        }));
      }
    }));

    // 3) 全forkの終了を待ってローダーOFF（UIは既に個別更新済み）
    yield all(forks.map(f => join(f)));
    const state = yield select(homePictreePhotoShootSelector);
    if (typeof state.countDenchu === 'number') {
      yield put(patchHomePictreePhotoShoot({ countAsset: state.countDenchu }));
    }
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataCheckInFromSxi(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!rangeTarget || rangeTarget.length !== 2) {
      console.error("Invalid rangeTarget");
      yield put(setPageTopLoader(false));
      return;
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }
    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", ");

    const sqlQueryCheckinSummary = {
      query: `
        SELECT COUNT(*) AS COUNT,
        COUNT(DISTINCT pae.power_asset_id) AS ASSET_COUNT,
        COUNT(DISTINCT pae.player_id) AS PLAYER_COUNT,
        MAX(ps.name) AS MOST_CHECKIN,
        MAX(pae.power_asset_id) AS MOST_ASSET
        FROM power_asset_event pae
        JOIN player_state ps ON pae.player_id = ps.player_id
        WHERE CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59' AND pae.type in ("CHECK_IN")
        AND pae.game_space_id IN (${seasonIdList})
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const sqlQueryCountUniqueActiveUser = {
      "query": `SELECT COUNT(DISTINCT deviceId)
                FROM DenchuGamePlayStartPmEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
                BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'`,
      "database": "KpiDashboard",
      "type": "list"
    };

    const [summaryTemp, dataUniqueActive, ] = yield all([
      call(
        getDataFromSxi,
        sqlQueryCheckinSummary,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
      call(getDataFromRDS, sqlQueryCountUniqueActiveUser, ENDPOINT.GET_DATA_USERS, jwtToken),
    ]);

    // // CheckIn Ratio
    const countUniqueActive = JSON.parse(dataUniqueActive.body);
    const countUniqueActiveValue = countUniqueActive[0][0]["longValue"];
    const checkInRatio = (summaryTemp[0]["PLAYER_COUNT"] / countUniqueActiveValue * 100).toFixed(2);

    const homeCheckInData = {
        checkInCount: summaryTemp[0]["COUNT"],
        checkInCountKisu: summaryTemp[0]["ASSET_COUNT"],
        checkInCountUniqueUser: summaryTemp[0]["PLAYER_COUNT"],
        checkInMost: summaryTemp[0]["MOST_ASSET"],
        checkInMostUser: summaryTemp[0]["MOST_CHECKIN"],
        checkInRatio: checkInRatio,
      };
    yield put(setHomePictreeCheckIn(homeCheckInData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataHomeReportTsuru(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!rangeTarget || rangeTarget.length !== 2) {
      console.error("Invalid rangeTarget");
      yield put(setPageTopLoader(false));
      return;
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }
    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", "); //"\'cm9jq577000034hpg5cngfzod\'"
    const rangeStartUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const rangeEndUtc = moment.tz(`${rangeTarget[1]} 23:59:59`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");

    // query to fetch all asset registred data
    // NOTE: deduplicate power_asset_id in power_asset_state first to reduce join volume.
    const sqlQueryCountAssetRegistered = {
      query: `SELECT COUNT(*) AS count
              FROM (
                SELECT DISTINCT pas.power_asset_id
                FROM power_asset_state pas
                WHERE pas.game_space_id IN (${seasonIdList})
              ) state_assets
              JOIN power_asset pa ON pa.id = state_assets.power_asset_id
              WHERE pa.type = 'POWER_POLE';`,
      database: "KpiDashboard",
      type: "list",
    };

    const sqlQueryCountAssetPlantRegistered = {
      query: `SELECT COUNT(*) AS count
              FROM (
                SELECT DISTINCT pas.power_asset_id
                FROM power_asset_state pas
                WHERE pas.game_space_id IN (${seasonIdList})
              ) state_assets
              JOIN power_asset pa ON pa.id = state_assets.power_asset_id
              WHERE pa.type = 'POWER_POLE'
                AND pa.sub_type = 'WITH_PLANTS';`,
      database: "KpiDashboard",
      type: "list",
    };

    const [tempAllAssetReg, tempPlantAssetReg] = yield all([
      call(getDataFromSxi,sqlQueryCountAssetRegistered,ENDPOINT.GET_DATA_SXI_DENCHU,jwtToken),
      call(getDataFromSxi,sqlQueryCountAssetPlantRegistered,ENDPOINT.GET_DATA_SXI_DENCHU,jwtToken),
    ]);
    const countAllAssetReg = tempAllAssetReg[0]["count"];
    const countPlantAssetReg = tempPlantAssetReg[0]["count"];

    // query1: 台帳あり/報告あり 撮影本数
    const sqlQueryCountReportTsuru = {
      query: `
        SELECT COUNT(DISTINCT pa.id) as 'LedgerTotal'
        FROM asset_event_of_review_approved aeora
        INNER JOIN power_asset_event pae ON pae.id = aeora.id
        INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
        INNER JOIN review_request rr ON rr.power_asset_id = pa.id
        INNER JOIN review_request_event rre ON rre.review_request_id = rr.id
        WHERE aeora.review_approved_count = '1'
        AND pae.game_space_id IN (${seasonIdList})
        AND pa.sub_type = 'WITH_PLANTS'
        AND rre.type = 'APPLIED'
        AND CONVERT_TZ(rre.occurred_datetime, 'UTC', 'Asia/Tokyo') BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
      `,
      database: "KpiDashboard",
      type: "list",
    };

    // query2: 台帳あり/報告なし 撮影本数
    const sqlQueryCountReportTsuruNoReport = {
      query: `
        SELECT COUNT(DISTINCT pa.id) as 'count-LedgerAndNoReport'
        FROM asset_event_of_review_approved aeora
        INNER JOIN power_asset_event pae ON pae.id = aeora.id
        INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
        INNER JOIN review_request rr ON rr.power_asset_id = pa.id
        INNER JOIN review_request_review_request_info_bind rrib ON rrib.review_request_id = rr.id
        INNER JOIN review_request_info rri ON rri.id = rrib.review_request_info_id
          AND rri.inspection_option_action_id = '152'
        WHERE aeora.review_approved_count = '1'
        AND pae.game_space_id IN (${seasonIdList})
        AND pa.sub_type = 'WITH_PLANTS'
        AND EXISTS (
          SELECT 1
          FROM review_request rr_period
          INNER JOIN review_request_event rre_period ON rre_period.review_request_id = rr_period.id
          WHERE rr_period.power_asset_id = pa.id
          AND rre_period.type = 'APPLIED'
          AND rre_period.occurred_datetime BETWEEN '${rangeStartUtc}' AND '${rangeEndUtc}'
        )
      `,
      database: "KpiDashboard",
      type: "list",
    };

    // query3: 台帳なし/報告あり 撮影本数
    const sqlQueryCountReportTsuruNoLedger = {
      // query3: 台帳なし台電柱の撮影本数
      query: `
        SELECT COUNT(*) as 'NoLedgerTotal'
        FROM (
          SELECT DISTINCT pae.power_asset_id
          FROM power_asset_event pae
          INNER JOIN asset_event_of_review_approved aeora ON aeora.id = pae.id
          INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
          WHERE aeora.review_approved_count = '1'
          AND pae.game_space_id IN (${seasonIdList})
          AND pa.sub_type = 'NORMAL'
        ) approved_normal_assets
        WHERE EXISTS (
          SELECT 1
          FROM review_request rr
          INNER JOIN review_request_event rre ON rre.review_request_id = rr.id
          WHERE rr.power_asset_id = approved_normal_assets.power_asset_id
          AND rre.type = 'APPLIED'
          AND rre.occurred_datetime BETWEEN '${rangeStartUtc}' AND '${rangeEndUtc}'
        )
      `,
      database: "KpiDashboard",
      type: "list",
    };

    // query4: 台帳なし/報告なし 撮影本数
    const sqlQueryCountReportTsuruNoLedgerNoReport = {
      query: `
        SELECT COUNT(*) as 'count-NoLedgerAndNoReport'
        FROM (
          SELECT DISTINCT rr.power_asset_id
          FROM review_request rr
          INNER JOIN review_request_event rre ON rre.review_request_id = rr.id
          INNER JOIN review_request_review_request_info_bind rrib ON rrib.review_request_id = rr.id
          INNER JOIN review_request_info rri ON rri.id = rrib.review_request_info_id
            AND rri.inspection_option_action_id = '152'
          WHERE rre.type = 'APPLIED'
        ) applied_152_assets
        INNER JOIN (
          SELECT DISTINCT pae.power_asset_id
          FROM power_asset_event pae
          INNER JOIN asset_event_of_review_approved aeora ON aeora.id = pae.id
          INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
          WHERE aeora.review_approved_count = '1'
          AND pae.game_space_id IN (${seasonIdList})
          AND pa.sub_type = 'NORMAL'
          AND EXISTS (
            SELECT 1
            FROM review_request rr_period
            INNER JOIN review_request_event rre_period ON rre_period.review_request_id = rr_period.id
            WHERE rr_period.power_asset_id = pae.power_asset_id
            AND rre_period.type = 'APPLIED'
            AND rre_period.occurred_datetime BETWEEN '${rangeStartUtc}' AND '${rangeEndUtc}'
          )
        ) approved_normal_assets
          ON approved_normal_assets.power_asset_id = applied_152_assets.power_asset_id
      `,
      database: "KpiDashboard",
      type: "list",
    };

    let tempLedgerAndReport;
    if (USE_MOCK_TSURU_LEDGER_TOTAL) {
      tempLedgerAndReport = MOCK_TSURU_LEDGER_TOTAL;
    } else {
      tempLedgerAndReport = yield call(getDataFromSxi, sqlQueryCountReportTsuru, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    }
    const tempLedgerNoReport = yield call(getDataFromSxi, sqlQueryCountReportTsuruNoReport, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    let tempNoLedgerReport;
    if (USE_MOCK_TSURU_NO_LEDGER_TOTAL) {
      tempNoLedgerReport = MOCK_TSURU_NO_LEDGER_TOTAL;
    } else {
      tempNoLedgerReport = yield call(getDataFromSxi, sqlQueryCountReportTsuruNoLedger, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    }
    let tempNoLedgerNoReport;
    if (USE_MOCK_TSURU_NO_LEDGER_NO_REPORT) {
      tempNoLedgerNoReport = MOCK_TSURU_NO_LEDGER_NO_REPORT;
    } else {
      tempNoLedgerNoReport = yield call(getDataFromSxi, sqlQueryCountReportTsuruNoLedgerNoReport, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    }

    const countLedgerAndReport = tempLedgerAndReport;
    const countLedgerNoReport = tempLedgerNoReport;
    const countNoLedgerReport = tempNoLedgerReport;
    const countNoLedgerNoReport = tempNoLedgerNoReport;
    const tsuruReportCount =
    (countLedgerAndReport[0]["LedgerTotal"]-countLedgerNoReport[0]["count-LedgerAndNoReport"])+countLedgerNoReport[0]["count-LedgerAndNoReport"];
    const homeReportTsuruData = {
      totalCount: tsuruReportCount,
      reportCount: (countLedgerAndReport[0]["LedgerTotal"]-countLedgerNoReport[0]["count-LedgerAndNoReport"])+(countNoLedgerReport[0]["NoLedgerTotal"]-countNoLedgerNoReport[0]["count-NoLedgerAndNoReport"]),
      totalRatio: (tsuruReportCount/countAllAssetReg*100).toFixed(1),
      tsuruOnlyRatio: (tsuruReportCount/countPlantAssetReg*100).toFixed(1),
      countLedgerAndReport: (countLedgerAndReport[0]["LedgerTotal"]-countLedgerNoReport[0]["count-LedgerAndNoReport"]),
      countLedgerAndNoReport: countLedgerNoReport[0]["count-LedgerAndNoReport"],
      countNoLedgerAndReport: (countNoLedgerReport[0]["NoLedgerTotal"]-countNoLedgerNoReport[0]["count-NoLedgerAndNoReport"]),
      countNoLedgerAndNoReport: countNoLedgerNoReport[0]["count-NoLedgerAndNoReport"],
      totalAssetReg: countAllAssetReg,
      totalPlantAssetReg: countPlantAssetReg,
      ledgerCount: (countLedgerAndReport[0]["LedgerTotal"]-countLedgerNoReport[0]["count-LedgerAndNoReport"]) + countLedgerNoReport[0]["count-LedgerAndNoReport"],
    };
    yield put(setHomePictreeReportTsuru(homeReportTsuruData));

    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_HOME_USER, getPictreeDataHomeUser);
  yield takeEvery(PICTREE.GET_HOME_CHECKIN, getPictreeDataCheckInFromSxi);
  yield takeEvery(PICTREE.GET_HOME_PHOTOSHOOT, getPictreeDataHomePhotoShootTepco);
  yield takeEvery(PICTREE.GET_HOME_REPORT_TSURU, getPictreeDataHomeReportTsuru);
}
